import useBlockedUsers from '@contexts/useBlockedUsers';
import {
  MessageStatus,
  QueryKeys,
  addOptimisticMessage,
  assertParameterIsNotUndefinedOrNull,
  confirmOptimisticMessage,
  conversationsApi,
  getMessages,
  removeFailedMessage,
  useAuthInfiniteQuery,
  useAuthMutation,
  useAuthQuery,
  type MessageWithStatus,
} from '@sunrise-ui/api-client';
import type {
  ConnectionType,
  Conversation,
  ConversationsApiGetConversationByUserIdsConversationsUsersGetRequest,
  ConversationsApiGetConversationConversationsConversationIdUsersUserIdGetRequest,
  ConversationsApiGetConversationUnreadConversationsConversationIdUnreadsGetRequest,
  CreateConversationInput,
  Message,
} from '@sunrise-ui/api/messenger';
import { ConversationType } from '@sunrise-ui/api/messenger';
import { useAuthStore, useSnackbar } from '@sunrise-ui/primitives';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

const {
  conversationsKey,
  messagesKey,
  unreadKey,
  unreadCountKey,
  messagesPageSize,
} = QueryKeys.conversations;
const { inboxKey } = QueryKeys.inbox;

export const useGetMessages = (conversationId: string) =>
  useAuthInfiniteQuery({
    queryFn: async ({ pageParam }) =>
      getMessages(conversationId, pageParam, messagesPageSize),
    queryKey: [messagesKey, conversationId, messagesPageSize],
    initialPageParam: 0,
    getNextPageParam(lastPage): number | undefined {
      if (lastPage?.next) return lastPage.offset + lastPage.limit;
    },
    getPreviousPageParam(lastPage): number | undefined {
      if (lastPage?.previous) return lastPage.offset - lastPage.limit;
    },
    staleTime: Infinity,
  });

type SendMessageArgs = {
  conversationId: string;
  user_id: string;
  content: string;
  client_id?: string;
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    Message,
    unknown,
    SendMessageArgs,
    {
      tempId: string;
    }
  >({
    async mutationFn({ conversationId, user_id, content, client_id }) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const response =
        await conversationsApi.sendMessageConversationsConversationIdMessagesPost(
          {
            conversationId,
            createMessageInput: {
              user_id,
              content,
              client_id: client_id ?? currentUserId,
            },
          },
        );
      return response.data;
    },
    onMutate: async ({ conversationId, user_id, content }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [messagesKey, conversationId, messagesPageSize],
      });

      // Cancel outgoing refetches (no need to snapshot since we use optimistic updates)

      // Create optimistic message with unique temp ID
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const optimisticMessage: MessageWithStatus = {
        _id: tempId,
        user_id,
        content,
        timestamp: new Date().toISOString(),
        conversation_id: conversationId,
        status: MessageStatus.PENDING,
      };

      // Add optimistic message to cache
      addOptimisticMessage(
        optimisticMessage,
        [messagesKey, conversationId, messagesPageSize],
        queryClient,
      );

      return { tempId };
    },
    onError: (_error, variables, context) => {
      // Remove failed message from cache and show snackbar if removed
      if (context?.tempId) {
        const messageWasRemoved = removeFailedMessage(
          context.tempId,
          [messagesKey, variables.conversationId, messagesPageSize],
          queryClient,
        );

        // Only show snackbar if we actually removed a message from view
        if (messageWasRemoved) {
          showSnackbar({
            text: 'Message failed to send. Please wait a few seconds and try again.',
            type: 'error',
          });
        }
      }
    },
    onSuccess: async (data, variables) => {
      // Confirm optimistic message with real data
      confirmOptimisticMessage(
        data.content,
        data,
        [messagesKey, variables.conversationId, messagesPageSize],
        queryClient,
      );

      // Update inbox
      await queryClient.invalidateQueries({ queryKey: [inboxKey] });
    },
  });
};

export const useCreateConversation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  // id comes as _id from BE even though the Conversation type doesn't specify it
  return useMutation<
    Conversation & { _id?: string },
    unknown,
    CreateConversationInput
  >({
    async mutationFn(createConversationInput: CreateConversationInput) {
      const response =
        await conversationsApi.createConversationConversationsPost({
          createConversationInput,
        });
      return response.data;
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: [conversationsKey] });
      await queryClient.invalidateQueries({ queryKey: [inboxKey] });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to start conversation. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useGetConversationUnread = ({
  conversationId,
}: ConversationsApiGetConversationUnreadConversationsConversationIdUnreadsGetRequest) =>
  useAuthQuery({
    async queryFn() {
      const response =
        await conversationsApi.getConversationUnreadConversationsConversationIdUnreadsGet(
          { conversationId },
        );
      return response.data;
    },
    queryKey: [conversationsKey, conversationId, unreadKey],
  });

export const useGetTotalUnreadCount = (connectionType: ConnectionType) => {
  const { currentUserId } = useAuthStore();
  return useAuthQuery({
    async queryFn() {
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const response =
        await conversationsApi.getTotalUnreadCountConversationsUsersUserIdUnreadsCountGet(
          {
            userId: currentUserId,
            connectionType,
          },
        );
      return response.data.count;
    },
    queryKey: [conversationsKey, currentUserId, unreadCountKey, connectionType],
  });
};

export const useMarkConversationRead = (conversationId: string) => {
  const queryClient = useQueryClient();
  const { currentUserId } = useAuthStore();

  return useAuthMutation({
    async mutationFn() {
      await conversationsApi.markConversationReadConversationsConversationIdMessagesReadPut(
        { conversationId },
      );
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: [inboxKey] });
      await queryClient.invalidateQueries({
        queryKey: [conversationsKey, conversationId, unreadKey],
      });
      await queryClient.invalidateQueries({
        queryKey: [conversationsKey, currentUserId, unreadCountKey],
      });
    },
  });
};

export const useGetConversationById = ({
  conversationId,
}: Pick<
  ConversationsApiGetConversationConversationsConversationIdUsersUserIdGetRequest,
  'conversationId'
>) => {
  const { isUserBlocked, isLoading: isLoadingBlockedUsers } = useBlockedUsers();
  const { currentUserId } = useAuthStore();

  const {
    data,
    isLoading: isLoadingConversation,
    ...otherProps
  } = useAuthQuery<Conversation & { _id?: string }>({
    async queryFn() {
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const response =
        await conversationsApi.getConversationConversationsConversationIdUsersUserIdGet(
          { conversationId, userId: currentUserId },
        );
      return response.data;
    },
    queryKey: [conversationsKey, conversationId],
    enabled: Boolean(conversationId),
  });

  const filteredData = useMemo(() => {
    const otherUserId = data?.users?.find((id) => id !== currentUserId);
    if (otherUserId && isUserBlocked(otherUserId)) return undefined;

    return data;
  }, [data, isUserBlocked]);

  return {
    isLoading: isLoadingConversation || isLoadingBlockedUsers,
    data: filteredData,
    ...otherProps,
  };
};

export const useGetConversationByUserIds = ({
  userIds,
  enabled,
}: ConversationsApiGetConversationByUserIdsConversationsUsersGetRequest & {
  enabled: boolean;
}) =>
  useAuthQuery<Conversation & { _id?: string }>({
    async queryFn() {
      const response =
        await conversationsApi.getConversationByUserIdsConversationsUsersGet({
          userIds,
        });
      return response.data;
    },
    queryKey: [conversationsKey, userIds],
    enabled: userIds.length === 2 && enabled,
    retry: false,
  });

export default function useStartOrGetConversation() {
  const { showSnackbar } = useSnackbar();

  return useMutation<
    Conversation & { _id?: string },
    unknown,
    { userId: string }
  >({
    async mutationFn({ userId }) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');

      const response = await conversationsApi
        .getConversationByUserIdsConversationsUsersGet({
          userIds: [userId, currentUserId],
        })
        .catch((error) => {
          if (error.response?.status === 404)
            return conversationsApi.createConversationConversationsPost({
              createConversationInput: {
                user_id: currentUserId,
                users: [userId, currentUserId],
                conversation_type: ConversationType.DirectMessage,
              },
            });

          throw error;
        });
      return response.data;
    },
    async onError() {
      showSnackbar({
        text: 'Failed to go to conversation. Please try again.',
        type: 'error',
      });
    },
  });
}

export const useReportMessage = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation({
    async mutationFn(messageId: string) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const response =
        await conversationsApi.reportMessageConversationsMessagesMessageIdReportPost(
          { reportMessageInput: { user_id: currentUserId }, messageId },
        );
      return response.data;
    },
    async onSuccess({ conversation_id }) {
      await queryClient.invalidateQueries({ queryKey: [inboxKey] });
      await queryClient.invalidateQueries({
        queryKey: [messagesKey, conversation_id, messagesPageSize],
      });
      showSnackbar({
        text: 'Reported and removed message from view',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to report message. Please try again',
        type: 'error',
      });
    },
  });
};
